// Customer identity (design/auth-v4.html): the API mints an anonymous
// identity for this device the first time checkout needs one and the
// device keeps it — the refresh token never expires. Zero questions before
// food. Verifying a mobile (a code by SMS) binds the phone to the identity —
// or, when another device verified that number first, moves this device to
// that identity so the person's history follows (design/auth-v4.html §6).

const KEY = 'rp-order-auth';

type Session = {
  accessToken: string;
  /** epoch ms */
  accessExpiresAt: number;
  refreshToken: string;
  userId: string;
  /** the mobile this identity proved by SMS; null until then */
  phoneE164?: string | null;
};

type User = { userId: string; phoneE164?: string | null };
type SessionView = { accessToken: string; accessExpiresAt: string; refreshToken: string; user: User };

function load(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function save(v: SessionView): Session {
  const s: Session = { accessToken: v.accessToken, accessExpiresAt: new Date(v.accessExpiresAt).getTime(), refreshToken: v.refreshToken, userId: v.user.userId, phoneE164: v.user.phoneE164 ?? null };
  localStorage.setItem(KEY, JSON.stringify(s));
  return s;
}

async function mint(): Promise<Session> {
  const resp = await fetch('/api/v1/auth/customers', { method: 'POST' });
  if (!resp.ok) throw new Error('Could not start a secure session.');
  return save((await resp.json()) as SessionView);
}

/** A valid customer access token — minted, refreshed, or reused as needed. */
export async function customerToken(): Promise<string> {
  const s = load();
  if (!s) return (await mint()).accessToken;
  if (Date.now() < s.accessExpiresAt - 60_000) return s.accessToken;
  const resp = await fetch('/api/v1/auth/sessions/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: s.refreshToken }),
  });
  if (!resp.ok) {
    // a broken session re-mints: an anonymous identity is cheap, and the
    // history behind the old one returns once the phone is bound
    localStorage.removeItem(KEY);
    return (await mint()).accessToken;
  }
  return save((await resp.json()) as SessionView).accessToken;
}

/** The identity this device holds, if any (for the account pages). */
export function customerUserId(): string | null {
  return load()?.userId ?? null;
}

/** The mobile this device's identity has verified, if any. */
export function verifiedPhone(): string | null {
  return load()?.phoneE164 ?? null;
}

/** After a verified phone: the same tokens, possibly a different identity behind them. */
export function adoptIdentity(user: User): void {
  const s = load();
  if (!s) return;
  localStorage.setItem(KEY, JSON.stringify({ ...s, userId: user.userId, phoneE164: user.phoneE164 ?? null }));
}
